import { acceptHMRUpdate, defineStore } from 'pinia'
import type {
  ApplyDiscount,
  ContactMethodEnum,
  Hash,
  Good as OriginalGood,
  PatchedUpdateQuotePricing,
  PaymentMethodEnum,
  PriceOption,
  QuoteByHash,
  QuoteByID,
  QuoteCreateByID,
  RouteDetailsRequest,
  UpdateQuoteSpecialRequirements, UpdateUserBillingType, Utm, Uuid, VehicleTypeEnum, Waypoint,
} from '@apiTypes'

import { getEffectiveVehicleWeight, getVehicleByType } from '@/utils/vehicle'
import { quoteTotalWeight } from '@/utils/quote.utils'
import api from '@api'

export interface Good extends OriginalGood {
  tempId?: number
}

// The by-id quote as the SPA holds it: its unsaved cargo rows carry a tempId, and
// QuoteByIDSerializer.to_representation adds the hash for a signed-in user (not in the schema).
export interface QuoteByIDState extends Omit<QuoteByID, 'goods'> {
  goods?: Good[]
  hash?: string
}

export const useQuoteStore = defineStore('quote', {
  state: () => ({
    quoteByID: {} as QuoteByIDState,
    quoteByHash: {} as QuoteByHash,
    loadingSpecialRequirements: false,
    routeDistance: 0,
    routeDuration: 0,

    // HERE found no route for the quote's waypoints: the quote form refuses to submit it.
    routeProblem: false,
  }),
  getters: {
    getQuoteByID: state => state.quoteByID,
    getQuoteByHash: state => state.quoteByHash,
    totalWeight: state => {
      const goodsWeight = quoteTotalWeight(state.quoteByID.goods || state.quoteByHash.goods)
      const vehicle = getVehicleByType(state.quoteByID.vehicle_type || state.quoteByHash.vehicle_type)
      const isTailLift = state.quoteByID.special_requirements?.tail_lift || state.quoteByHash.special_requirements?.tail_lift

      const weight = getEffectiveVehicleWeight(vehicle, isTailLift)

      return goodsWeight || weight
    },
  },
  actions: {
    setLoadingSpecialRequirements(loading: boolean) {
      this.loadingSpecialRequirements = loading
    },
    async validateWaypoints(vehicleType: VehicleTypeEnum, pickup: Waypoint, delivery: Waypoint) {
      const response = await api.validateWaypoints.pricingValidateWaypointsCreate({ vehicle_type: vehicleType, pickup, delivery })

      return response.data
    },
    async fetchQuoteByHash(hash: string) {
      const response = await api.hash.quotesByHashRetrieve(hash)

      this.quoteByHash = response.data as QuoteByHash

      return response.data
    },
    async createQuote(payload: Partial<QuoteCreateByID>) {
      // The view reads the body with QuoteCreateByIDSerializer; the schema names QuoteByID for it.
      const response = await api.quotesByIdCreate(payload as unknown as QuoteByID)
      if (response)
        this.quoteByID = response.data
    },
    async updateQuote(params: Uuid.QuotesByIdPartialUpdate.RequestBody) {
      const response = await api.uuid.quotesByIdPartialUpdate(this.quoteByID.uuid as string, params)
      if (response) {
        this.quoteByID = { ...this.quoteByID, ...response.data }

        // A by-hash copy of this quote (loaded by /free-quote/<hash>) predates
        // the update and the offers it may just have created, so drop it and
        // let the magic link load the quote again.
        if (this.quoteByHash.uuid && this.quoteByHash.uuid === this.quoteByID.uuid)
          this.quoteByHash = {} as QuoteByHash
      }
    },
    async updateUtmParams(utmParams: Utm) {
      const response = await api.uuid.quotesByIdUpdateUtmCreate(this.quoteByID.uuid as string, utmParams)
      if (response)
        this.quoteByID = { ...this.quoteByID, ...response.data }
    },
    async markMagicLinkVisited() {
      return await api.hash.quotesByHashMarkMagicLinkVisitedCreate(this.quoteByHash.hash as string)
    },
    async updateQuoteByHash(params: Hash.QuotesByHashPartialUpdate.RequestBody) {
      const response = await api.hash.quotesByHashPartialUpdate(this.quoteByHash.hash as string, params)
      if (response)
        this.quoteByHash = { ...this.quoteByHash, ...response.data }
    },
    async confirmQuote(hash: string) {
      const response = await api.hash.quotesByHashConfirmCreate(hash)
      if (response)
        this.quoteByHash = response.data
    },
    addGood(good: Partial<Good>) {
      const tempId = Date.now()

      const newGood = {
        ...good,
        tempId,
      }

      if (!this.quoteByID.goods)
        this.quoteByID.goods = []

      this.quoteByID.goods.push(newGood as Good)
    },
    isMatchingGood(good: Good, updatedGood: Partial<Good>) {
      const matchesTempId = updatedGood.tempId !== undefined && good.tempId === updatedGood.tempId
      const matchesId = updatedGood.id !== undefined && good.id === updatedGood.id

      return matchesTempId || matchesId
    },
    updateGood(updatedGood: Partial<Good>) {
      if (!this.quoteByID.goods)
        this.quoteByID.goods = []

      this.quoteByID.goods = this.quoteByID.goods.map(good =>
        this.isMatchingGood(good, updatedGood) ? { ...good, ...updatedGood } : good,
      )
    },
    removeGood(identifier: number) {
      if (!this.quoteByID.goods)
        this.quoteByID.goods = []

      this.quoteByID.goods = this.quoteByID.goods.filter((g: Good) => g.tempId !== identifier && g.id !== identifier)
    },
    setDistance(distance: number) {
      this.routeDistance = distance
    },
    setDuration(duration: number) {
      this.routeDuration = duration
    },
    setRouteProblem(problem: boolean) {
      this.routeProblem = problem
    },
    resetQuote() {
      this.quoteByID = {} as QuoteByIDState
      this.routeProblem = false
    },
    async updateWaypoint(waypoint: Waypoint, index?: number) {
      if (!this.quoteByHash.waypoints)
        this.quoteByHash.waypoints = []

      if (index !== undefined && index !== -1) {
        const updatedWaypoint = await api.uuid.quotesByIdUpdateWaypointCreate(this.quoteByID.uuid as string, waypoint)

        this.quoteByHash.waypoints[index] = { ...this.quoteByHash.waypoints[index], ...updatedWaypoint.data }
      }
      else {
        this.quoteByHash.waypoints.push(waypoint)
      }
    },
    async updateWaypoints(waypoints: Partial<Waypoint>[]) {
      const response = await api.hash.quotesByHashUpdateQuoteWaypointsCreate(this.quoteByHash.hash as string, { waypoints })

      this.quoteByHash = response.data

      return response.data
    },
    updateContactMethod(method: ContactMethodEnum) {
      this.quoteByID.contact && (this.quoteByID.contact.contact_method = method)
    },
    async routeDetails(data: RouteDetailsRequest) {
      const response = await api.uuid.quotesByIdRouteDetailsCreate(this.quoteByID.uuid as string, data)
      if (response) {
        this.quoteByID.polyline = response.data.polyline
        this.quoteByID.distance = String(response.data.distance)
      }

      return response.data
    },
    async updatePricing(data: PatchedUpdateQuotePricing) {
      return api.uuid.quotesByIdUpdatePricingPartialUpdate(this.quoteByID.uuid as string, data)
    },
    setQuoteCustom(custom: boolean) {
      this.quoteByID.quote_custom = custom
    },
    setQuoteLoadingStatus(status: QuoteByID['loading_status']) {
      this.quoteByID.loading_status = status
    },
    fetchSessionId(hash: string) {
      return api.checkoutSession.paymentsCheckoutSessionCreate(hash)
    },
    async updateBilling(hash: string, billing_info: object) {
      const response = await api.hash.quotesByHashUpdateBillingCreate(hash, { billing_info })

      this.quoteByHash = response.data

      return response.data
    },
    async updatePayment(hash: string, payment_method: PaymentMethodEnum) {
      const response = await api.hash.quotesByHashUpdatePaymentCreate(hash, { payment_method })

      this.quoteByHash = response.data

      return response.data
    },
    autofillCompany(hash: string, vat_number: string) {
      return api.hash.quotesByHashAutofillCompanyCreate(hash, {
        vat_number,
      })
    },
    runCofaceTask(hash: string) {
      return api.hash.quotesByHashCofaceTaskCreate(hash)
    },
    async selectPriceOption(hash: string, priceOption: PriceOption) {
      const response = await api.hash.quotesByHashUpdateSelectedPriceOptionCreate(hash, { selected_price_option: priceOption })

      this.quoteByHash = response.data

      return response.data
    },
    async updateSpecialRequirements(hash: string, specialRequirements: UpdateQuoteSpecialRequirements) {
      const response = await api.hash.quotesByHashUpdateSpecialRequirementsCreate(hash, specialRequirements)

      this.quoteByHash = response.data

      return response.data
    },
    async fetchQuoteWithOrder(hash: string) {
      const response = await api.hash.quotesByHashWithOrderRetrieve(hash)

      // The order page keeps the QuoteWithOrder here and reads it back as one (pages/order/[hash].vue).
      this.quoteByHash = response.data as unknown as QuoteByHash

      return response.data
    },
    async updateQuoteUserType(hash: string, userType: UpdateUserBillingType) {
      const response = await api.hash.quotesByHashUpdateQuoteUserTypeCreate(hash, userType)

      this.quoteByHash = response.data

      return response.data
    },
    async applyDiscount(payload: ApplyDiscount) {
      const response = await api.apply.discountsApplyCreate(payload)
      if (response)
        this.quoteByHash = response.data
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useQuoteStore, import.meta.hot))
